"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";

const pillars = [
    { key: "build", number: "01" },
    { key: "augment", number: "02" },
    { key: "sell", number: "03" },
];


export default function VisionSection() {
    const t = useTranslations();

    return (
        <section className="relative overflow-hidden">
            {/* ─── Atmospheric background ───────────────────────────── */}
            <div
                aria-hidden
                className="absolute inset-0 -z-10 pointer-events-none"
            >
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-primary/[0.04] dark:bg-primary/[0.07] blur-[120px]" />
                <div
                    className="absolute inset-0 opacity-[0.025] dark:opacity-[0.06] text-gray-900 dark:text-white"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
            </div>

            <div className="relative max-w-6xl mx-auto px-6 pt-36 sm:pt-48 md:pt-[440px] pb-24 md:pb-40">
                {/* ═══════════════════════════════════════════════════ */}
                {/*  MANIFESTO                                          */}
                {/* ═══════════════════════════════════════════════════ */}
                <div id="vision" className="scroll-mt-32 text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                        className="font-sourceSerif text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[1.02] tracking-[-0.02em] text-gray-900 dark:text-white"
                    >
                        <span className="block">
                            {t("VisionSection_manifesto_line1")}
                        </span>
                        <span className="block italic text-primary mt-3">
                            {t("VisionSection_manifesto_line2")}
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, delay: 0.35 }}
                        className="mt-12 text-lg sm:text-xl text-gray-600 dark:text-grayDark-400 max-w-2xl mx-auto leading-[1.7]"
                    >
                        {t("VisionSection_statement")}
                    </motion.p>
                </div>

                {/* ═══════════════════════════════════════════════════ */}
                {/*  THREE PILLARS — typographic editorial               */}
                {/* ═══════════════════════════════════════════════════ */}
                <div className="mt-24 md:mt-44">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 lg:gap-14">
                        {pillars.map((pillar, index) => (
                            <motion.article
                                key={pillar.key}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{
                                    duration: 0.7,
                                    delay: index * 0.15,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="group relative"
                            >
                                {/* Numéro en outline énorme */}
                                <span
                                    aria-hidden
                                    className="block font-sourceSerif italic text-[7rem] sm:text-[9rem] md:text-[11rem] lg:text-[12rem] leading-[0.8] text-transparent select-none transition-all duration-500 group-hover:text-primary/5"
                                    style={{
                                        WebkitTextStroke: "1.5px #E8511D",
                                    }}
                                >
                                    {pillar.number}
                                </span>

                                <div className="mt-8 md:mt-10 max-w-sm">
                                    <h3 className="font-sourceSerif text-3xl md:text-[2rem] leading-tight text-gray-900 dark:text-white tracking-tight">
                                        {t(
                                            `VisionSection_pillar_${pillar.key}_title`,
                                        )}
                                    </h3>

                                    <div className="mt-4 w-10 h-[2px] bg-primary origin-left transition-transform duration-500 ease-out group-hover:scale-x-[2.5]" />

                                    <p className="mt-5 text-[15px] text-gray-600 dark:text-grayDark-400 leading-[1.7]">
                                        {t(
                                            `VisionSection_pillar_${pillar.key}_description`,
                                        )}
                                    </p>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
}
